import React, { useState, useEffect } from 'react';
import '../../App.css';
import { useAuthContext } from '../../contexts/AuthContext.jsx';
import Loading from '../../components/Loading.jsx';
import { useLocation, useNavigate } from "react-router-dom";
import posthog from 'posthog-js';
import { extractIdFromPath, saveSearchParams, loadSearchParams } from '../../libs/searchParamsStorage.js';

const AnalyticsContent = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { organization, profile, getMbJwt, setCurrentMetabaseUrl, currentAnalyticsHomepage } = useAuthContext();
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeLoading, setIframeLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeRef, setIframeRef] = useState(null);
  const analyticsOrigin = import.meta.env.VITE_ANALYTICS_ORIGIN;

  
  // Clear context when component unmounts or when not on analytics page
  useEffect(() => {
    return () => {
      setCurrentMetabaseUrl('');
    };
  }, [setCurrentMetabaseUrl]);
  
  const defaultParams = new URLSearchParams({
    logo: 'false',
    top_nav: 'true',
    search: 'true',
    new_button: organization?.permissions?.analytics == 'edit' ? 'true' : 'false',
    data_picker: 'staged',
    entity_types: ['table', 'question'],
  });

  const readOnly = profile?.permissions?.analytics == 'view';

  useEffect(() => {
    posthog.capture('$pageview');
  }, []);

  // Set up message listener for iframe communication
  useEffect(() => {
    const callback = (event) => {
      // Only process messages from the Metabase analytics origin
      if (event.origin !== analyticsOrigin) {
        return;
      }
      
      // Additional validation: ensure we have the expected data structure
      if (!event.data || typeof event.data !== 'object' || !event.data.metabase) {
        return;
      }
      
      const metabaseEvent = event.data.metabase;
      document.body.style.overflow = 'hidden';
      if (import.meta.env.DEV) {
        console.log('🔄 Metabase event:', metabaseEvent);
      }

      // Handle location changes
      if (metabaseEvent.type === 'location') {
        // Handle root path redirect
        if (metabaseEvent.location?.pathname === '/' && metabaseEvent.location?.pathname !== currentAnalyticsHomepage) {
          if (import.meta.env.DEV) {  
            console.log('🔄 Redirecting iframe to ', currentAnalyticsHomepage);
          }
          
          // Try sending a message to the iframe
          if (iframeRef && iframeRef.contentWindow) {
            try {
              iframeRef.contentWindow.postMessage({
                metabase: {
                  type: 'location',
                  location: {
                    ...metabaseEvent.location,
                    pathname: currentAnalyticsHomepage
                  }
                }
              }, analyticsOrigin);
              
            } catch (error) {
              console.error('🔄 Error sending message to iframe:', error);
            }
          } else {
            console.warn('🔄 Iframe ref not available for redirection');
          }
        } else if (
          readOnly &&
          metabaseEvent.location?.pathname.startsWith('/dashboard/') &&
          metabaseEvent.location?.search === ''
        ) {
          if (import.meta.env.DEV) {  
            console.log('🔄 Read Only User, clearing search params on dashboard');
          }
          const dashboardId = extractIdFromPath(metabaseEvent.location?.pathname);
          const searchParams = loadSearchParams(dashboardId) ?? '?ignore=true';
          const redirectUrl = `https://viz.muffindata.com/dashboard/${dashboardId}${searchParams}`;
          // Try sending a message to the iframe
          if (iframeRef && iframeRef.contentWindow) {
            try {

              iframeRef.contentWindow.postMessage({
                metabase: {
                  type: 'location',

                  //URL encode the redirect URL
                  location: `https://viz.muffindata.com/redirect?url=${encodeURIComponent(redirectUrl)}`
                }
              }, analyticsOrigin);
              
            } catch (error) {
              console.error('🔄 Error sending message to iframe:', error);
            }
          } else {
            console.warn('🔄 Iframe ref not available for redirection');
          }
        }

        else if (
          readOnly &&
          metabaseEvent.location?.pathname === '/redirect'
        ) {

          //Get URL from search params
          const url = metabaseEvent.location?.search.split('=')[1];
          //Decode the URL
          const decodedUrl = decodeURIComponent(url);
          if (import.meta.env.DEV) {  
            console.log('🔄 Redirecting iframe to ', decodedUrl);
          }
          // Try sending a message to the iframe
          if (iframeRef && iframeRef.contentWindow) {
            try {

              iframeRef.contentWindow.postMessage({
                metabase: {
                  type: 'location',

                  //URL encode the redirect URL
                  location: decodedUrl
                }
              }, analyticsOrigin);
              
            } catch (error) {
              console.error('🔄 Error sending message to iframe:', error);
            }
          } else {
            console.warn('🔄 Iframe ref not available for redirection');
          }
        }

        else if (
          metabaseEvent.location.pathname &&
          !metabaseEvent.location?.pathname.endsWith('/notebook') &&
          !metabaseEvent.location?.pathname.endsWith('/question')
        ) {
          // Build the URL without default params
          let newIframeUrl = metabaseEvent.location.pathname;

          // Store the current Metabase URL for potential homepage setting
          setCurrentMetabaseUrl(newIframeUrl);
          
          //Save params for dashboard or question
          if (
            readOnly &&
            metabaseEvent.location.search !== '' &&
            metabaseEvent.location.search !== '?ignore=true' &&
            (
              metabaseEvent.location.pathname.startsWith('/dashboard')
              || metabaseEvent.location.pathname.startsWith('/question')
            )
          ) {
            // Extract ID and save search parameters if this is a dashboard or question
            const id = extractIdFromPath(metabaseEvent.location.pathname);
            if (id) {
              saveSearchParams(id, metabaseEvent.location.search);
            }
          }
          
          // Add hash first (if it exists)
          if (metabaseEvent.location.hash) {
            newIframeUrl += metabaseEvent.location.hash;
          }
          
          // Add search params, but filter out default params
          if (metabaseEvent.location.search) {
            const searchParams = new URLSearchParams(metabaseEvent.location.search);
            
            // Remove default params to keep only custom ones
            defaultParams.forEach((value, key) => {
              searchParams.delete(key);
            });
            
            // Only add search if there are remaining custom params
            if (searchParams.toString()) {
              newIframeUrl += `?${searchParams.toString()}`;
            }
          }

          // Store the current Metabase URL for potential homepage setting
          if (import.meta.env.DEV) {
            console.log('🔄 Setting current Metabase URL:', newIframeUrl);
          }

        sessionStorage.setItem('returnTo', newIframeUrl);
        } else {
          // Clear the current Metabase URL for potential homepage setting
          setCurrentMetabaseUrl('');
        }
      }
    };

    // Listen on the main window for messages from the iframe
    window.addEventListener('message', callback);

    // Cleanup function
    return () => {
      window.removeEventListener('message', callback);
      document.body.style.overflow = 'auto';
    };
  }, [iframeRef, analyticsOrigin, defaultParams]);

  useEffect(() => {
    const getIframeUrl = async () => {
      if (!analyticsOrigin) {
        setError('Analytics origin not configured');
        return;
      }
      
      if (!organization) {
        setError('No current organization');
        return;
      }

      try {
        const jwt = await getMbJwt();
        if (!jwt) {
          setError('Failed to get Metabase JWT');
          return;
        }

        let finalIframeUrl = '';
        let returnTo = currentAnalyticsHomepage; // Default to root
        
        // Priority order: state.link > saved returnTo > profile homepage > root
        const savedIframeUrl = state?.link || sessionStorage.getItem('returnTo');
        // TODO: Implement when Supabase profile integration is ready
        // const homepage = getHomepage();

        if (savedIframeUrl) {
          returnTo = savedIframeUrl.includes('?') ? `${savedIframeUrl}&${defaultParams}` : `${savedIframeUrl}?${defaultParams}`;
        } else {
          // TODO: Add homepage fallback when Supabase profile integration is ready
          // } else if (homepage) {
          //   returnTo = homepage.includes('?') ? `${homepage}&${defaultParams}` : `${homepage}?${defaultParams}`;
          returnTo = `/?${defaultParams}`;
          sessionStorage.setItem('returnTo', '/');
        }
        
        finalIframeUrl = `${analyticsOrigin}/auth/sso?jwt=${jwt}&return_to=${encodeURIComponent(returnTo)}`;

        if (import.meta.env.DEV) {
          console.log('🔗 Built iframe URL:', {
            analyticsOrigin,
            jwtLength: jwt.length,
            returnTo,
            // TODO: Add homepage when Supabase profile integration is ready
            // homepage,
            finalUrl: finalIframeUrl.substring(0, 150) + '...'
          });
        }

        setIframeUrl(finalIframeUrl);
        setError(null);
      } catch (error) {
        console.error('Error building iframe URL:', error);
        setError(`Failed to build iframe URL: ${error.message}`);
      }
    }

    if (organization) {
      getIframeUrl();
    }

    if (organization && !organization?.permissions?.analytics) {
      navigate('/home');
    }
  }, [organization, navigate]);

  // Handle iframe body overflow
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Show error if something went wrong
  if (error) {
    return (
      <div className="flex items-center justify-center h-92vh">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Unable to Load Analytics</h2>
          <p className="text-gray-600 mb-4">
            There was an issue loading your analytics dashboard. This may be due to permissions or configuration settings.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Please contact support if this issue persists.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {!iframeUrl ? <Loading /> :
        <>
          {iframeLoading ? <Loading /> : null}
          
          {/* Set Homepage Button - Hidden for now */}
          {/* TODO: Show when Supabase profile integration is ready */}
          {false && showSetHomepageButton && (
            <div className="fixed top-4 right-4 z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">Set as Analytics Homepage?</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Would you like to set this page as your default analytics homepage?
                  </p>
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={setCurrentPageAsHomepage}
                      disabled={updatingHomepage}
                      className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updatingHomepage ? 'Setting...' : 'Set as Homepage'}
                    </button>
                    <button
                      onClick={() => setShowSetHomepageButton(false)}
                      disabled={updatingHomepage}
                      className="px-3 py-1.5 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <iframe
            ref={setIframeRef}
            src={iframeUrl}
            width="100%"
            style={{ height: '92vh', display: iframeLoading ? 'none' : 'block' }}
            onLoad={() => {
              if (import.meta.env.DEV) {
                console.log('🔄 Iframe loaded with URL:', iframeUrl);
                console.log('🔄 Current iframe src:', document.querySelector('iframe')?.src);
              }
              setIframeLoading(false);
            }}
            onError={(e) => {
              if (import.meta.env.DEV) {
                console.error('❌ Iframe error:', e);
              }
              setError('Failed to load iframe content');
            }}
            title="Metabase Analytics Dashboard"
          />
        </>
      }
    </>
  );
};

export default function Analytics() {
  return (
    <AnalyticsContent />
  );
}
